/**
 * S2 — DINERO (verdad única).
 *
 * Reglas duras (ver ADR-003):
 *  1. NUNCA se usa `number` de coma flotante para dinero en dominio, motor, BD ni API.
 *     Internamente: `bigint` en unidades menores (centavos).
 *  2. En el cable (JSON) viaja como entero seguro (≤ Number.MAX_SAFE_INTEGER) para que
 *     el cliente no necesite BigInt; el límite práctico es ~90 billones de unidades.
 *  3. Todo redondeo es explícito: `mulDivFloor` (piso) favorece a la casa — documentado
 *     en §8.1 del plan. Nunca `Math.round` suelto.
 *  4. Las cuotas se expresan en puntos base (bp) enteros: 10_000 bp = 1.00x.
 *
 * La regla de lint `@thepit/no-raw-money` prohíbe `number` en campos con sufijo
 * `_cents`/`Cents` fuera de este archivo y del formateador de UI.
 */
import { z } from 'zod';

/** Unidades menores por unidad mayor. Todo el sistema asume 2 (centavos). */
export const MINOR_UNITS_PER_MAJOR = 100;

/** Puntos base: 1 bp = 0.01%. 10_000 bp = 1x. */
export const BPS_DENOMINATOR = 10_000n;

export type Cents = bigint;
export type BasisPoints = number;

/** Moneda. Se congela por pelea en `rules_snapshot` (nunca en settings globales). */
export const CurrencySchema = z.object({
  /** Código corto mayúsculas. Puede ser ISO 4217 (ARS, USD) o ficción (PIT, RP$). */
  code: z
    .string()
    .regex(/^[A-Z]{2,5}$/, 'Código de moneda: 2 a 5 letras mayúsculas'),
  /** Símbolo para display: $, ⚡$, 🪙 */
  symbol: z.string().min(1).max(4),
  /** Decimales en display (0–4). El almacenamiento siempre es en unidades menores. */
  decimals: z.number().int().min(0).max(4),
});
export type Currency = z.infer<typeof CurrencySchema>;

/** Moneda por defecto del sistema: dólares de Los Santos (RP FiveM). */
export const DEFAULT_CURRENCY: Currency = { code: 'USD', symbol: '$', decimals: 2 };

/** Entero no negativo seguro — formato de cable para centavos. */
export const CentsWireSchema = z
  .number()
  .int('Los centavos deben ser enteros')
  .nonnegative('Los centavos no pueden ser negativos')
  .max(Number.MAX_SAFE_INTEGER, 'Monto fuera del rango representable');

/** Entero positivo seguro — para apuestas (> 0). */
export const PositiveCentsWireSchema = CentsWireSchema.min(1, 'El monto debe ser mayor a 0');

/** Cuota en puntos base: > 0. 10_000 = 1.00x. */
export const OddsBpSchema = z
  .number()
  .int('La cuota debe ser entera (bp)')
  .positive('La cuota debe ser > 0')
  .max(1_000_000, 'Cuota fuera de rango razonable (máx 100x)');

/** Rake en puntos base: 0..5000 (0%..50%). */
export const RakeBpSchema = z
  .number()
  .int('El rake debe ser entero (bp)')
  .min(0)
  .max(5_000, 'El rake no puede superar el 50%');

/* -------------------------------------------------------------------------- */
/* Conversión                                                                  */
/* -------------------------------------------------------------------------- */

/** Convierte el entero de cable a bigint de dominio, validando el rango. */
export function wireToCents(wire: number): Cents {
  if (!Number.isSafeInteger(wire) || wire < 0) {
    throw new RangeError(`Centavos de cable inválidos: ${String(wire)}`);
  }
  return BigInt(wire);
}

/** Convierte bigint de dominio a entero de cable, validando el rango. */
export function centsToWire(cents: Cents): number {
  if (cents < 0n) throw new RangeError('Los centavos no pueden ser negativos');
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError('Los centavos exceden el rango seguro de JSON');
  }
  return Number(cents);
}

/** Igual que `centsToWire` pero admite negativos (resultado neto de la casa). */
export function centsToWireSigned(cents: Cents): number {
  const abs = cents < 0n ? -cents : cents;
  if (abs > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError('Los centavos exceden el rango seguro de JSON');
  }
  return Number(cents);
}

/**
 * Convierte centavos a su representación decimal como texto ("1500.50"), SIN usar
 * `toFixed` ni floats. Es lo que usan los exportes (CSV, FiveM, Discord).
 */
export function centsToMajorString(cents: number, decimals = 2): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const factor = 10 ** decimals;
  const major = Math.trunc(abs / factor);
  const minor = abs % factor;
  const sign = negative ? '-' : '';
  return decimals > 0 ? `${sign}${major}.${String(minor).padStart(decimals, '0')}` : `${sign}${major}`;
}

/**
 * `(a * b) / c` con redondeo **hacia abajo** (piso), en aritmética entera exacta.
 * Es la primitiva de todo el motor de pagos. `c` no puede ser 0.
 */
export function mulDivFloor(a: Cents, b: bigint, c: bigint): Cents {
  if (c === 0n) throw new RangeError('mulDivFloor: división por cero');
  return (a * b) / c;
}

/** Aplica una tasa en puntos base con piso: `floor(value * bp / 10000)`. */
export function applyBps(value: Cents, bp: BasisPoints): Cents {
  return mulDivFloor(value, BigInt(bp), BPS_DENOMINATOR);
}

/** Cuota en bp a partir de dos montos: `floor(payout * 10000 / stake)`. */
export function oddsBpFrom(stake: Cents, payout: Cents): BasisPoints {
  if (stake <= 0n) throw new RangeError('oddsBpFrom: stake debe ser > 0');
  return Number(mulDivFloor(payout, BPS_DENOMINATOR, stake));
}

/** Payout exacto para una cuota en bp: `floor(stake * bp / 10000)`. */
export function payoutFromOddsBp(stake: Cents, bp: BasisPoints): Cents {
  return applyBps(stake, bp);
}

/* -------------------------------------------------------------------------- */
/* Parseo y formato (borde de UI)                                              */
/* -------------------------------------------------------------------------- */

/**
 * Parsea lo que escribe un humano ("1.500,50", "1500.5", "1500") a centavos.
 * Acepta separador decimal `.` o `,` y separadores de miles. Nunca usa floats para
 * el resultado: construye el entero por partes.
 */
export function parseMoneyInput(input: string, decimals = 2): Cents {
  const cleaned = input.trim().replace(/\s/g, '').replace(/[^\d.,-]/g, '');
  if (cleaned === '' || cleaned === '-') throw new RangeError('Monto vacío');

  const negative = cleaned.startsWith('-');
  const body = negative ? cleaned.slice(1) : cleaned;

  const lastDot = body.lastIndexOf('.');
  const lastComma = body.lastIndexOf(',');
  let decimalSep = -1;
  if (lastDot !== -1 && lastComma !== -1) {
    decimalSep = Math.max(lastDot, lastComma);
  } else if (lastDot !== -1) {
    // Un solo '.' con 1–`decimals` dígitos detrás se interpreta como decimal.
    const after = body.length - lastDot - 1;
    decimalSep = after > 0 && after <= decimals ? lastDot : -1;
  } else if (lastComma !== -1) {
    const after = body.length - lastComma - 1;
    decimalSep = after > 0 && after <= decimals ? lastComma : -1;
  }

  let intPart: string;
  let fracPart: string;
  if (decimalSep === -1) {
    intPart = body;
    fracPart = '';
  } else {
    intPart = body.slice(0, decimalSep);
    fracPart = body.slice(decimalSep + 1);
  }

  intPart = intPart.replace(/[.,]/g, '');
  fracPart = fracPart.replace(/[.,]/g, '').slice(0, decimals).padEnd(decimals, '0');

  if (intPart === '' && fracPart === '') throw new RangeError('Monto inválido');
  const digits = (intPart === '' ? '0' : intPart) + fracPart;
  const value = BigInt(digits);
  return negative ? -value : value;
}

/** Formatea centavos para display. Solo en el borde de UI. */
export function formatMoney(
  cents: Cents,
  currency: Currency = DEFAULT_CURRENCY,
  locale = 'es-AR',
): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const factor = 10n ** BigInt(currency.decimals);
  const major = abs / factor;
  const minor = abs % factor;
  const majorStr = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(major);
  const minorStr =
    currency.decimals > 0 ? '.' + minor.toString().padStart(currency.decimals, '0') : '';
  return `${negative ? '-' : ''}${currency.symbol}${majorStr}${minorStr}`;
}

/** Formatea una cuota en bp: 18500 ⇒ "1.85x". */
export function formatOdds(bp: BasisPoints, locale = 'es-AR'): string {
  const value = bp / 10_000;
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}x`;
}

/** Formatea un rake en bp: 500 ⇒ "5.00%". */
export function formatBpsPercent(bp: BasisPoints, locale = 'es-AR'): string {
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(bp / 100)}%`;
}

/** Suma segura de una lista de centavos. */
export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce<Cents>((acc, v) => acc + v, 0n);
}
